import { useMemo, useState } from 'react';

import { Plus } from 'lucide-react';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useReports, useCreateReport, useDeleteReport } from '@/hooks/useReports';
import { useUrlSearchParams } from '@/hooks/useUrlSearchParams';
import { apiClient } from '@/lib/api/axios';
import { generateReportPDF } from '@/lib/report-pdf';

import { CreateReportDialog } from './CreateReportDialog';
import { ReportsTable } from './ReportsTable';

import type { ReportRecord, ReportQuery } from '@repo/shared-types';

export const ReportsListPage = () => {
  usePageTitle('Reports');
  const { searchParams, updateParams } = useUrlSearchParams();
  const limit = 10;
  const page = Number(searchParams.get('page')) || 1;

  const query = useMemo(() => {
    const q: Partial<ReportQuery> = { page, limit };
    return q;
  }, [page, limit]);

  const { data, isLoading } = useReports(query);
  const reports = data?.data ?? [];
  const pagination = data?.pagination;

  const createMutation = useCreateReport();
  const deleteMutation = useDeleteReport();
  const { toast } = useToast();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ReportRecord | null>(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({
        variant: 'success',
        title: 'Report deleted',
        description: 'Report has been deleted successfully.',
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete report. Please try again.',
      });
    }
  };

  const handleExportPDF = async (report: ReportRecord) => {
    try {
      // Fetch comments for the report
      const response = await apiClient.get(`/reports/${report.id}/comments`);
      const comments = response.data || [];

      generateReportPDF(report, comments);
      toast({
        variant: 'success',
        title: 'PDF generated',
        description: 'Report PDF has been downloaded.',
      });
    } catch (error) {
      // If comments fetch fails, generate PDF without comments
      console.error('Failed to fetch comments:', error);
      generateReportPDF(report, []);
      toast({
        variant: 'success',
        title: 'PDF generated',
        description: 'Report PDF has been downloaded.',
      });
    }
  };

  return (
    <div className="container mx-auto space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Reports</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Generate and manage performance reports
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Report
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportsTable
            reports={reports}
            isLoading={isLoading}
            pagination={pagination}
            onPageChange={(newPage) => updateParams({ page: String(newPage) })}
            onCreateClick={() => setIsCreateDialogOpen(true)}
            onExportPDF={handleExportPDF}
            onDelete={setDeleteTarget}
          />
        </CardContent>
      </Card>

      <CreateReportDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={async (dto) => {
          await createMutation.mutateAsync(dto);
          setIsCreateDialogOpen(false);
          toast({
            variant: 'success',
            title: 'Report generated',
            description: 'Report has been generated successfully.',
          });
        }}
        isPending={createMutation.isPending}
      />

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        resourceName="report"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
